import type { BestTimeDef } from '@/lib/best-time-pages';
import { SectionShell } from '@/components/layout/SectionShell';
import { getEventsForCountry, getCountryByCode, getCountries, getDestinationMonths, getDestinationMonth } from '@/lib/data-source';
import { getOriginVacationWindows } from '@/lib/cross-calendar';
import { DESTINATIONS } from '@/lib/country-month-pages';
import { CrossCalendarPillLinks } from './CrossCalendarPillLinks';
import { buildBestTimeJsonLd, buildArticleJsonLd } from '@/lib/seo';
import { SITE_LAUNCH_DATE } from '@/lib/seo/constants';
import { SITE_URL } from '@/lib/brand';
import { toAbsoluteImageUrl, getCountryOgImage } from '@/lib/images';
import { JsonLdScript } from '@/components/lib/JsonLdScript';
import { FaqBlock } from './FaqBlock';
import { SeasonOverviewGrid } from './SeasonOverviewGrid';
import { MonthConditionsChart } from './MonthConditionsChart';
import { MonthGuideGrid } from './MonthGuideGrid';
import { PeakFestivalsList } from './PeakFestivalsList';
import { BestTimeSummaryTable } from './BestTimeSummaryTable';
import { CALENDAR_YEAR } from '@/lib/config';
import { ui, uiFb, getTemplateStr } from '@/lib/i18n';
import type { EvergreenSlugEntry } from '@/lib/i18n';
import { Flag } from '@/components/ui/Flag';
import type { CountryCode } from '@/lib/types';

const YEAR = CALENDAR_YEAR;

interface BestTimeSeoSectionProps {
  def: BestTimeDef;
  entry: EvergreenSlugEntry;
  siblings?: Array<{ displayName: string; slug: string }>;
}

export function BestTimeSeoSection({ def, entry, siblings }: BestTimeSeoSectionProps) {
  const lang = entry.lang ?? 'en';
  const country = getCountryByCode(def.countryCode);
  const countryName = country?.name ?? def.countryCode;
  // Get cultural festivals during peak season for dynamic content
  const countryEvents = getEventsForCountry(def.countryCode);
  const peakFestivals = countryEvents.filter(e => {
    const month = parseInt(e.dateStart.slice(5, 7), 10);
    return def.bestMonths.includes(month) && e.type === 'cultural_festival';
  }).slice(0, 6);

  // Climate data for peak month (for JSON-LD enrichment)
  const peakMonth = def.bestMonths[0];
  const peakDm = peakMonth ? getDestinationMonth(def.countryCode, peakMonth) : null;

  // Build enriched FAQ combining existing + origin-aware questions
  const originCountriesWithPeakHolidays = getCountries()
    .filter((c: { code: CountryCode }) => c.code !== def.countryCode)
    .filter((c: { code: CountryCode }) =>
      def.bestMonths.some(m => getOriginVacationWindows(c.code, m).holidays.length > 0)
    )
    .map((c: { name: string }) => c.name);

  // "Cheapest month" FAQ — find low-price seasons
  const cheapSeasons = def.seasons.filter(s => s.prices === 'Low');
  const cheapMonthNames = cheapSeasons
    .flatMap(s => s.months)
    .map(m => ui('month_' + m, lang))
    .slice(0, 4)
    .join(', ');
  const cheapSeasonName = cheapSeasons[0]?.name ?? '';

  const enrichedFaq = [
    ...def.faq,
    ...(peakDm ? [{
      question: getTemplateStr('seo.bt.faq_peak_q', lang, { month: ui('month_' + peakMonth, lang), destination: def.displayName }),
      answer: getTemplateStr('seo.bt.faq_peak_a', lang, { month: ui('month_' + peakMonth, lang), crowds: peakDm.crowdLevel, prices: peakDm.priceLevel, weather: String(peakDm.weatherScore), verdict: peakDm.visitVerdict }),
    }] : []),
    ...(cheapSeasons.length > 0 ? [{
      question: getTemplateStr('seo.bt.faq_cheapest_q', lang, { destination: def.displayName }),
      answer: getTemplateStr('seo.bt.faq_cheapest_a', lang, { destination: def.displayName, months: cheapMonthNames, season: cheapSeasonName }),
    }] : []),
    ...(originCountriesWithPeakHolidays.length > 0 ? [{
      question: getTemplateStr('seo.bt.faq_countries_q', lang, { destination: def.displayName }),
      answer: getTemplateStr('seo.bt.faq_countries_a', lang, { countries: originCountriesWithPeakHolidays.slice(0, 8).join(', '), destination: def.displayName }),
    }] : []),
  ];

  // JSON-LD
  const touristJsonLd = buildBestTimeJsonLd(def.displayName, def, entry.slug, peakDm, def.countryCode);
  const articleJsonLd = buildArticleJsonLd({
    headline: `${entry.h2Title} ${YEAR}`,
    url: `${SITE_URL}/${entry.slug}`,
    image: toAbsoluteImageUrl(getCountryOgImage({ code: def.countryCode })),
    datePublished: SITE_LAUNCH_DATE,
    dateModified: new Date().toISOString().slice(0, 10),
    lang,
    articleSection: 'Travel',
  });

  return (
    <SectionShell as="section" className="pb-8 pt-12">
      {/* JSON-LD structured data */}
      <JsonLdScript data={touristJsonLd} />
      <JsonLdScript data={articleJsonLd} />

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-text-primary md:text-3xl">
          {entry.h2Title} {YEAR}
        </h1>
        <p className="mt-2 text-sm text-text-secondary md:text-base">
          <Flag code={def.countryCode} size={16} /> {countryName} — {getTemplateStr('seo.bt.season_guide', lang, { year: String(YEAR) })}
        </p>
      </div>

      <BestTimeSummaryTable seasons={def.seasons} bestMonths={def.bestMonths} lang={lang} />
      <SeasonOverviewGrid seasons={def.seasons} lang={lang} />
      <MonthConditionsChart destMonths={getDestinationMonths(def.countryCode)} bestMonths={def.bestMonths} lang={lang} />
      <MonthGuideGrid months={def.months} bestMonths={def.bestMonths} lang={lang} />
      <PeakFestivalsList festivals={peakFestivals} lang={lang} />

      {/* Plan from Your Country */}
      <div className="mb-8 border-t border-border-subtle pt-6">
        <details>
          <summary className="cursor-pointer">
            <h2 className="inline font-heading text-xl font-bold text-text-primary md:text-2xl">
              {uiFb('ui.plan_from_your_country', 'Plan from Your Country', lang)}
            </h2>
            <span className="ml-2 text-xs text-text-secondary">{uiFb('ui.click_to_expand', '(click to expand)', lang)}</span>
          </summary>
          <p className="mt-2 text-sm text-text-secondary">
            {getTemplateStr('seo.bt.holidays_align', lang, { destination: def.displayName })}
          </p>
          <div className="mt-4 space-y-3">
            {getCountries()
              .filter((c: { code: CountryCode }) => c.code !== def.countryCode)
              .map((c: { code: CountryCode; name: string; flag: string }) => {
                const bestOverlaps = def.bestMonths
                  .map(m => {
                    const vw = getOriginVacationWindows(c.code, m);
                    const dm = getDestinationMonth(def.countryCode, m);
                    return { month: m, vw, dm };
                  })
                  .filter(o => o.vw.holidays.length > 0);

                if (bestOverlaps.length === 0) return null;

                const topOverlap = bestOverlaps.reduce((best, cur) =>
                  cur.vw.totalDaysOff > best.vw.totalDaysOff ? cur : best
                );

                return (
                  <div key={c.code} className="content-card p-3">
                    <div className="flex items-center gap-2">
                      <Flag code={c.code} size={20} />
                      <span className="text-sm font-medium text-text-primary">{c.name}</span>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-text-secondary">
                      {uiFb('bt.origin_overlap', 'Your holidays in {month} ({holidays}) align with {destination}\'s {season}.', lang, { month: ui('month_' + topOverlap.month, lang), holidays: topOverlap.vw.holidays.map(h => h.title).join(', '), destination: def.displayName, season: topOverlap.dm?.seasonName ?? uiFb('bt.travel_season', 'travel season', lang) })}
                      {topOverlap.vw.longWeekends.length > 0 && ` ${uiFb('bt.long_weekends_available', '{count} long weekend(s) available.', lang, { count: String(topOverlap.vw.longWeekends.length) })}`}
                    </p>
                  </div>
                );
              })
              .filter(Boolean)}
          </div>
        </details>
      </div>

      {/* Cross-calendar links */}
      {(() => {
        const destDef = DESTINATIONS.find(d => d.countryCode === def.countryCode);
        if (!destDef) return null;
        return (
          <CrossCalendarPillLinks
            slugPrefix={destDef.slugPrefix}
            countryCode={def.countryCode}
            displayName={def.displayName}
            lang={lang}
            linkTemplateKey="seo.bt.origin_holidays_link"
          />
        );
      })()}

      {/* Sibling cross-links (e.g. Bali ↔ Indonesia) */}
      {siblings && siblings.length > 0 && (
        <div className="mb-8 border-t border-border-subtle pt-6">
          <p className="text-sm text-text-secondary">
            {uiFb('ui.also_see', 'Also see:', lang)}{' '}
            {siblings.map((s, i) => (
              <span key={s.slug}>
                {i > 0 && ', '}
                <a href={`/${s.slug}`} className="font-medium text-text-link hover:underline">
                  {uiFb('ui.best_time', 'Best Time', lang)} {s.displayName} {YEAR}
                </a>
              </span>
            ))}
          </p>
        </div>
      )}

      {/* FAQ */}
      <FaqBlock faqs={enrichedFaq} heading={uiFb('ui.faq_heading', 'Frequently Asked Questions', lang)} className="border-t border-border-subtle pt-6" />
    </SectionShell>
  );
}
